import { SCHEDULE_URL } from '@/constants/navigation';
import { SCENARIO_TYPES } from '@/constants/scenarios';
import { getTransferPair } from '@/services/transferPairs';
import { useBenchmarkingStore } from '@/stores/benchmarking-store';
import { useScenarioStore } from '@/stores/scenario-store';
import { BenchmarkingEmailPayload, CompareEmailPayload, ModelType } from '@/types/model';
import { ReportFormValues } from '@/types/reportSchema';
import type { TransferPair } from '@/types/transfer-pair';
import {
  getBenchmarkingPresentation,
  getScenarioPresentation,
  ScenarioPresentation,
} from '@/utils/scenario-presentation';

export const mapPresentationToEmail = (presentation: ScenarioPresentation, type: ModelType) => ({
  title: presentation.title,
  sections: (Object.values(presentation.sections) as any[]).map(section => ({
    title: section.title,
    items: Object.entries(section)
      .filter(([key, item]) => (
        key !== 'title' &&
        key !== 'icon' &&
        item !== undefined &&
        item !== null &&
        (type !== 'compare' || key !== 'exchangeRate')
      ))
      .map(([_, item]) => ({
        label: (item as any).label,
        value: (item as any).value,
      })),
  })),
  highlight: presentation.highlight,
});

export async function buildComparePayload({
  values,
  inputs,
  data,
  visibleScenarios,
  getCountryName,
  t,
  formatters,
}: {
  values: ReportFormValues;
  inputs: ReturnType<typeof useScenarioStore.getState>['inputs'];
  data: TransferPair;
  visibleScenarios: ReturnType<typeof useScenarioStore.getState>['visibleScenarios'];
  getCountryName: (id: string | undefined) => string;
  t: any;
  formatters: any;
}): Promise<CompareEmailPayload> {
  const { formatCurrency, formatPercentage, formatNumber } = formatters;
  return {
    title: t('report.compare.title'),
    description: t('report.compare.description'),
    header: {
      name: values.name,
      email: values.email,
      vendorIdList: {
        label: t('onboarding.summary.vendorCountryList'),
        value: inputs.vendorIdList.map(id => getCountryName(id)),
      },
      customerId: { label: t('onboarding.summary.customerOrigin'), value: getCountryName(inputs.customerId) },
      vendorId: { label: t('onboarding.summary.vendorDestination'), value: getCountryName(inputs.vendorId) },
      invoicingAmount: {
        label: t('onboarding.fields.invoicingAmount'),
        value: `${formatNumber(inputs?.invoicingAmount ?? 0)} ${inputs.invoicingCurrency}`,
      },
      exchangeRate: {
        label: t('scenarios.card.exchangeRate'),
        value: `USD/${data.originCurrencyISO} ${data.exchangeRate != null ? formatNumber(data.exchangeRate, 2) : '-'}`,
      },
      invoicingConcept: {
        label: t('onboarding.summary.invoicingConcept'),
        value: t(`concepts.${inputs.invoicingConcept}.label`),
      },
      sellThroughLocalPartner: {
        label: t('onboarding.fields.localPartnerQuestion'),
        value: inputs.sellThroughLocalPartner ? t('common.yes') : t('common.no'),
      },
      partnerMargin: inputs.sellThroughLocalPartner
        ? { label: t('onboarding.summary.localPartnerMargin'), value: `${inputs.partnerMargin}%` }
        : undefined,
    },
    alsoThere: {
      title: t('home.compare.cta.scheduleAlsoThere.title'),
      description: t('home.compare.cta.scheduleAlsoThere.description'),
      button: t('home.compare.cta.scheduleAlsoThere.button'),
      url: SCHEDULE_URL,
    },
    comparisonTable: {
      headers: {
        advantages: t('report.compare.table.headers.advantages'),
        internationalInvoicing: t('report.compare.table.headers.internationalInvoicing'),
        localPartner: t('report.compare.table.headers.localPartner'),
        localSubsidiary: t('report.compare.table.headers.localSubsidiary'),
        alsoThere: t('report.compare.table.headers.alsoThere'),
      },
      rows: [
        { label: t('report.compare.table.rows.row1'), values: [false, true, true, true] },
        { label: t('report.compare.table.rows.row2'), values: [false, true, true, true] },
        { label: t('report.compare.table.rows.row3'), values: [false, true, true, true] },
        { label: t('report.compare.table.rows.row4'), values: [false, true, true, true] },
        { label: t('report.compare.table.rows.row5'), values: [false, true, true, true] },
        { label: t('report.compare.table.rows.row6'), values: [false, true, true, true] },
        { label: t('report.compare.table.rows.row7'), values: [false, true, false, true] },
        { label: t('report.compare.table.rows.row8'), values: [false, false, false, true] },
        { label: t('report.compare.table.rows.row9'), values: [true, false, true, true] },
        { label: t('report.compare.table.rows.row10'), values: [true, false, true, true] },
        { label: t('report.compare.table.rows.row11'), values: [false, true, false, true] },
        { label: t('report.compare.table.rows.row12'), values: [true, false, false, true] },
        { label: t('report.compare.table.rows.row13'), values: [true, false, false, true] }
      ]
    },
    scenarios: (SCENARIO_TYPES)
      .filter(type => visibleScenarios.has(type) && type !== 'alsoThere')
      .map(type => {
        const presentation = getScenarioPresentation(type, data, inputs, t, {
          formatCurrency,
          formatPercentage,
          formatNumber,
        });
        return mapPresentationToEmail(presentation, 'compare');
      }),
  };
}

export async function buildBenchmarkingPayload({
  values,
  benchmarkingCommon,
  benchmarkingItems,
  getCountryName,
  t,
  formatters,
}: {
  values: ReportFormValues;
  benchmarkingCommon: ReturnType<typeof useBenchmarkingStore.getState>['commonProps'];
  benchmarkingItems: ReturnType<typeof useBenchmarkingStore.getState>['items'];
  getCountryName: (id: string | undefined) => string;
  t: any;
  formatters: any;
}): Promise<BenchmarkingEmailPayload> {
  const { formatCurrency, formatPercentage, formatNumber } = formatters;
  return {
    header: {
      name: values.name,
      email: values.email,
      vendorIdList: benchmarkingCommon.vendorIdList
        ? {
          label: t('onboarding.summary.vendorCountryList'),
          value: benchmarkingCommon.vendorIdList.map(id => getCountryName(id)),
        }
        : undefined,
      scenario: {
        label: t('onboarding.fields.scenario'),
        value: benchmarkingCommon.scenario ? t(`scenarios.types.${benchmarkingCommon.scenario}.title`) : '',
      },
      partnerMargin:
        benchmarkingCommon.scenario === 'localPartner'
          ? {
            label: t('onboarding.summary.localPartnerMargin'),
            value: `${benchmarkingCommon.partnerMargin}%`,
          }
          : undefined,
      invoicingAmount: {
        label: t('onboarding.fields.invoicingAmount'),
        value: `${formatNumber(benchmarkingCommon.invoicingAmount ?? 0)} USD`,
      },
    },
    scenarios: benchmarkingCommon.scenario
      ? (
        await Promise.all(
          benchmarkingItems.map(async item => {
            if (!item.customerId || !item.vendorId || !item.invoicingConcept) return null;

            const data = await getTransferPair({
              concept: item.invoicingConcept,
              customerId: item.customerId,
              vendorId: item.vendorId,
              invoicingCurrency: 'USD',
            });

            if (!data) return null;

            const presentation = getBenchmarkingPresentation(benchmarkingCommon, item, data, t, {
              formatCurrency,
              formatPercentage,
              formatNumber,
            });

            if (!presentation) return null;

            return {
              header: {
                customer: { label: t('onboarding.summary.customerOrigin'), value: getCountryName(item.customerId) },
                vendor: { label: t('onboarding.summary.vendorDestination'), value: getCountryName(item.vendorId) },
                invoicingConcept: {
                  label: t('onboarding.summary.invoicingConcept'),
                  value: item.invoicingConcept ? t(`concepts.${item.invoicingConcept}.label`) : '',
                },
              },
              ...mapPresentationToEmail(presentation, 'benchmarking'),
            };
          }),
        )
      ).filter((s): s is NonNullable<typeof s> => s !== null)
      : [],
  };
}
